import { DestroyRef, EventEmitter, Injectable } from '@angular/core';
import { ParamMap, Router } from '@angular/router';
import { CartInterface, CartEventType, RegisterData, AnyCartEvent } from './cart.interface';
import { Person } from '../entities/person';
import { Cart } from '../entities/cart';
import { Shop, UnbookableReason, unbookableType } from '../../../../shared/entities/shop';
import { AgeIntervalInterface } from '../../../../shared/interfaces/age-interval-interface';
import { BookingParameterService } from '../../../../core/services/booking-parameter.service';
import { Addon, Product } from '../entities';
import { Locale, SupportedCurrency } from '@frontend';
import {
  catchError,
  debounceTime,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  ReplaySubject,
  Subject,
  switchMap,
  take
} from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { delayedRetry } from '../../../../../../projects/platform-manager/src/lib/services/DEFAULT_MAX_RETRIES';
import { codetype } from '../../../../shared/entities/bookingParameters';
import { AmountInterface } from '../../../../layout/interfaces/amount-interface';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { PlatformInformationService } from '../../../../../../projects/platform-manager/src/public_api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpHeaders } from '@angular/common/http';
import { NotificationService } from '../../../../shared/services/notification.service';
import { DatePipe } from '@angular/common';
import { PartnerLinkService } from '../../../../core/services/partner-link.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  CoreService,
  LegacyAccessoryDto,
  LegacyDynamicPricesRequestDto,
  LegacyDynamicPricesResponseDto,
  LegacyEquipmentDto,
  LegacyInsuranceDto,
  LegacyPersonDto
} from '../../generated';
import { AbTestService } from '../../../../shared/abtest';

@Injectable({
  providedIn: 'root'
})
export class CartService {
  public cart = new Cart(); //: Cart;
  public cart$ = new ReplaySubject<AnyCartEvent>(1);
  protected defaultAgeAdult = 36;
  public getProductsWithDiscounts$ = new Subject<AnyCartEvent>(); // trigger discount fetch from outer components
  private noMansSki =
    !this.platformService.getCurrentPartner() &&
    this.platformService.getCurrentPlatformShortcode() === 'ar' &&
    this.abTestService.getVersion('noMansSki') === 'v2'
      ? 'v2'
      : 'v1';
  public latestProductsWithDiscounts$ = this.getProductsWithDiscounts$.pipe(
    // discount worthy update occurred, do some magic
    switchMap(event => {
      return this.smartDiscountUpdate(event);
    }),
    // debounce in case of quick changes in succession,
    // not as serious on person less selection
    debounceTime(this.noMansSki === 'v2' ? 0 : 500),
    switchMap(update => {
      // only fetch discounts when they have been deemed necessary
      if (this.platformInformationService.getIsBrowser() && update.isDiscountFetchRequired) {
        return this.fetchDiscountsFromCore(
          this.translateService.currentLang as Locale
          // this.currencyService.actualCurrency as Currency - if we ever want make the system worse, switch to non-euro based calculations
        );
      } else {
        // SSR should never call the discount api since it depends on user input and location and request date and can not be cached.
        return of(update.event.cart);
      }
    })
  );

  private cartWorker?: Worker;

  constructor(
    private translateService: TranslateService,
    private bookingParameterService: BookingParameterService,
    private platformService: PlatformInformationService,
    private cookieService: SsrCookieService,
    private coreService: CoreService,
    private platformInformationService: PlatformInformationService,
    private notificationService: NotificationService,
    private datePipe: DatePipe,
    private partnerLinkService: PartnerLinkService,
    private localizeRouterService: LocalizeRouterService,
    private router: Router,
    private abTestService: AbTestService
  ) {
    if (this.platformInformationService.getIsBrowser()) {
      this.cartWorker = new Worker(new URL('../../../../cart-idb.worker.ts', import.meta.url));
      this.cartWorker.onmessage = (event: MessageEvent<{ eventType: string; cartFromIDB: Cart }>) => {
        if (event.data.eventType === 'FETCHED_FROM_IDB' && event.data.cartFromIDB) {
          this.cart.registerData = event.data.cartFromIDB.registerData;
          this.cart.persons.forEach((p, index) => {
            const IDBPerson = event.data.cartFromIDB.persons[index];
            if (IDBPerson) {
              p.name = IDBPerson.name;
              p.birthday = IDBPerson.birthday;
              p.weight = IDBPerson.weight;
              p.height = IDBPerson.height;
              p.shoe_size = IDBPerson.shoe_size;
              p.skill = IDBPerson.skill;
            }
          });
          this.updateCart(CartEventType.cartInitialisedByIDB);
        }
      };
    }
  }

  /**
   * In case we can not serve an available product selection for the given search params,
   * we redirect the customers to the townpage to make an alternative choice. To hit the SSR cache,
   * we strip the search params and cart since the data is stored in the customers booking params (localStorage)
   * @param shop
   */
  private navigateToTownPage(shop: Shop) {
    const route = this.partnerLinkService
      .getUrlReplacingPartnerAfterPrefix(
        this.localizeRouterService.translateRoute(
          '/SKIHIREENTRY/' + shop.townSlugs[this.translateService.currentLang]
        ),
        this.localizeRouterService.translateRoute('/SKIHIREENTRY')
      )
      .replace('partner-', 'partner-/');
    this.router.navigate([route], {
      queryParams: {
        cart: null,
        startDate: null,
        endDate: null
      }
    });
  }

  private handleUnbookableReasons(unbookableReasons: UnbookableReason[], shop: Shop) {
    let transkey = '';
    let transvars = {};
    const dateFormat = this.translateService.instant('CORE.notificationDateFormat');

    switch (unbookableReasons[0].type) {
      case unbookableType.minDuration:
        transkey = 'NOTIFICATION.shopMinDuration';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          min_duration: unbookableReasons[0].minDuration
        };
        break;
      case unbookableType.maxDuration:
        transkey = 'NOTIFICATION.shopMaxDuration';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          max_duration: unbookableReasons[0].maxDuration
        };
        break;
      case unbookableType.minDelay:
        transkey = 'NOTIFICATION.shopMinDelay';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          min_delay: unbookableReasons[0].minDelay
        };
        break;
      case unbookableType.after:
        transkey = 'NOTIFICATION.shopAfterSeason';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US')
        };
        break;
      case unbookableType.before:
        transkey = 'NOTIFICATION.shopBeforeSeason';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          start_date: this.datePipe.transform(unbookableReasons[0].start, dateFormat, undefined, 'en-US')
        };
        break;
      case unbookableType.pause:
        transkey = 'NOTIFICATION.shopPause';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          start_date: this.datePipe.transform(unbookableReasons[0].start, dateFormat, undefined, 'en-US'),
          end_date: this.datePipe.transform(unbookableReasons[0].end, dateFormat, undefined, 'en-US')
        };
        break;
      case unbookableType.unservableAges:
        transkey = 'NOTIFICATION.shopUnservableAges';
        transvars = {
          shop_name: shop.name,
          shop_address: shop.address,
          age: unbookableReasons[0].age
        };
        break;
    }

    if (transkey) {
      const confirmEmitter = new EventEmitter<null>();
      confirmEmitter.pipe(take(1)).subscribe(() => {
        this.navigateToTownPage(shop);
      });

      this.notificationService.add({
        displayTime: 0,
        transkey,
        transvars,
        confirmation: [
          {
            type: 'primary',
            confirmationTrans: 'CORE.notification.ok',
            confirmationEmitter: confirmEmitter
          }
        ],
        id: 'blocker'
      });
    }
  }

  /**
   * Tells you if changing number of people would result in a different
   * group discount 'group'
   * @param shop the shop to compare for
   * @param personCountA how many people 'before'
   * @param personCountB how many people 'after'
   * @returns true if the group discount changed in any way
   */
  private isGroupDiscountForShopDifferent(shop: Shop, personCountA: number, personCountB: number): boolean {
    if (shop?.discountPromotions && !Array.isArray(shop?.discountPromotions)) {
      const groupDiscounts = shop.discountPromotions.group
        .filter(d => d.type === 'group')
        .sort((a, b) => b.criterion - a.criterion);
      const prev = groupDiscounts.find(d => d.criterion <= personCountA);
      const now = groupDiscounts.find(d => d.criterion <= personCountB);
      if (prev !== now && prev?.criterion !== now?.criterion) {
        return true;
      }
    }

    return false;
  }

  /**
   * Checks the query parameters passed, and if they are valid it will create a cart based on those
   * Otherwise, it will fallback to the default cart from bookingParams
   * Later on it will also check the IndexedDb to see if we can get something from there
   */
  public initCart(params: ParamMap, shop: Shop, products: Product[], destroyRef?: DestroyRef) {
    this.cart.mapDataFrom(this.sanitizeParams(params, shop, products), shop, products);
    if (this.platformInformationService.getIsBrowser()) {
      const unbookableReasons = shop.unbookableReasonsFor(
        this.cart.startDate,
        this.cart.endDate,
        this.cart.persons.map(p => p.age)
      );
      if (unbookableReasons.length) {
        this.handleUnbookableReasons(unbookableReasons, shop);
      }
      this.cartWorker?.postMessage({ eventType: 'GET', shopId: this.cart.shop.id.toString() });
    }
    if (destroyRef) {
      const eventMeaningfulForIDBUpdate = [
        CartEventType.cartInitialised,
        CartEventType.couponcodeUpdated,
        CartEventType.dateRangeUpdated,
        CartEventType.personAdded,
        CartEventType.personAgeUpdated,
        CartEventType.personNameUpdated,
        CartEventType.personSkillChanged,
        CartEventType.personRemoved,
        CartEventType.productRemoved,
        CartEventType.registerDataUpdated,
        CartEventType.productUpdated,
        CartEventType.promocodeUpdated
      ];
      this.cart$
        .pipe(
          filter(ev => eventMeaningfulForIDBUpdate.includes(ev.eventType)),
          debounceTime(500),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(() => {
          this.cartWorker?.postMessage({ eventType: 'PUT', cart: this.cart });
        });
    }

    // for no person products, we need to remove all people without any, even if that
    // makes the cart void of any person
    if (this.noMansSki === 'v2') {
      this.cart.persons = this.cart.persons.filter(p => p.products.length != 0);
      this.cart.selectedPerson = new Person();
      this.cart.selectedPerson.skill = 'advanced';
      this.cart.selectedPerson.ageInterval = shop.ageIntervals.find(ai => ai.is_default);
    }

    this.updateCart(CartEventType.cartInitialised);
  }

  /**
   * sets selected person and fires event.
   * Will only send event if the selectedPerson changes
   * @param index
   */
  public selectPerson(index: number) {
    if (
      index >= 0 && // not negative
      index <= this.cart.persons.length && // within range
      this.cart.persons.indexOf(this.cart.selectedPerson) !== index // not the same
    ) {
      this.cart.selectedPerson = this.cart.persons[index];
      this.updateCart(CartEventType.personSelected);
    }
  }

  /**
   * It will add a new person
   */
  public addPerson(shop: Shop) {
    const person = new Person();
    person.ageInterval = shop.ageIntervals.find(ai => ai.is_default);
    person.age = person.ageInterval.start === 0 ? person.ageInterval.end : person.ageInterval.start;

    this.cart.persons.push(person);
    this.cart.selectedPerson = person;

    this.updateCart(CartEventType.personAdded);
  }

  public updateRegisterData(registerData: RegisterData) {
    this.cart.registerData = registerData;
    this.updateCart(CartEventType.registerDataUpdated);
  }

  public copyPastePerson(person: Person) {
    const newPerson = new Person();
    newPerson.ageInterval = person.ageInterval;
    newPerson.age = person.age;
    person.productEntities.forEach(productToCopy => {
      newPerson.productEntities.push(new Product(productToCopy));
    });
    this.cart.persons.push(newPerson);
    if (this.noMansSki === 'v1') {
      this.cart.selectedPerson = person;
    }

    this.updateCart(CartEventType.personAdded);
  }

  /**
   * It will update the age of the person at given index
   */
  public updatePersonAge(ageInterval: AgeIntervalInterface, index?: number) {
    let person;
    if (index !== undefined) {
      person = this.cart.persons[index];
    } else {
      person = this.cart.selectedPerson;
    }
    person.age = ageInterval.start !== 0 ? ageInterval.start : ageInterval.end;
    person.ageInterval = ageInterval;
    // we need to drop equipment that is unfit for the new ageInterval
    for (let i = person.productEntities.length - 1; i >= 0; i--) {
      if (person.productEntities[i].ageCategoryId !== person.ageInterval.age_category.id) {
        person.productEntities.splice(i, 1);
      }
    }

    this.updateCart(CartEventType.personAgeUpdated, person);
  }

  /**
   * It will update the name of the person at given index
   */
  public updatePersonName(name: string | undefined, index: number) {
    this.cart.persons[index].name = name?.trim();
    this.updateCart(CartEventType.personNameUpdated);
  }

  /**
   * It will remove the person at given index.
   * If needed, will also update the selected person
   */
  public removePerson(index: number) {
    this.cart.persons.splice(index, 1);
    if (this.noMansSki === 'v1') {
      if (this.cart.persons.indexOf(this.cart.selectedPerson) === -1) {
        if (index === 0) {
          this.cart.selectedPerson = this.cart.persons[0];
        } else if (index === this.cart.persons.length) {
          this.cart.selectedPerson = this.cart.persons[this.cart.persons.length - 1];
        } else {
          this.cart.selectedPerson = this.cart.persons[index];
        }
      }
    }

    this.updateCart(CartEventType.personRemoved);
  }

  /**
   * It will add a product to the person at given index
   */
  public updateProduct(person: Person, product: Product) {
    const prod = new Product(product);
    // find out if product is already in person and therefore needs replacement
    let replaceProduct = person.productEntities.find(p => {
      return (
        p.definitionId === product.definitionId || // same product -> update to addons
        p.productCategoryId === product.productCategoryId // category is the same and..
      );
    });

    // replace the item since it is updated
    if (replaceProduct) {
      replaceProduct = person.productEntities.splice(
        person.productEntities.findIndex(pE => pE.definitionId === replaceProduct.definitionId),
        1,
        prod
      )[0];
    } else {
      // add a new one
      person.productEntities.push(prod);
    }

    this.updateCart(CartEventType.productUpdated, person, product, replaceProduct);
  }

  /**
   * It will remove a product to the person at given index
   */
  public removeProduct(person: Person, productId: number) {
    const productIndex = person.productEntities.findIndex(p => p.definitionId === productId);
    if (productIndex > -1) {
      const product = person.productEntities.splice(productIndex, 1);
      this.updateCart(CartEventType.productRemoved, person, product[0]);
    }
  }

  /**
   * Adds a new person with given product. Automatically sets the age interval
   * to match given product info.
   * @param shop
   * @param product
   */
  public addNewPersonWithProduct(shop: Shop, product: Product) {
    const prod = new Product(product);
    const person = new Person();
    person.ageInterval = shop.ageIntervals.find(ai => prod.ageCategoryId === ai.age_category_id);
    person.age = person.ageInterval.start === 0 ? person.ageInterval.end : person.ageInterval.start;
    person.productEntities.push(prod);
    this.cart.persons.push(person);
    this.cart.selectedPerson = new Person();
    this.cart.selectedPerson.skill = 'advanced';
    this.cart.selectedPerson.ageInterval = person.ageInterval;
    this.cart.selectedPerson.age = person.age;
    this.updateCart(CartEventType.productUpdated, person, product);
  }

  /**
   * sets a new rental date range and will emit an xxx event
   * @param range
   */
  public updateDateRange(range: { start: Date; end: Date }) {
    this.bookingParameterService.bookingParameters.start = range.start;
    this.bookingParameterService.bookingParameters.end = range.end;
    this.bookingParameterService.saveBookingParameters(this.bookingParameterService.bookingParameters);
    this.cart.startDate = range.start;
    this.cart.endDate = range.end;
    this.updateCart(CartEventType.dateRangeUpdated);
  }

  /**
   * set or remove ('') promocode from cart
   * @param code the promotionCode to set
   */
  public setPromocode(code: string) {
    this.cart.promotionCode = code;
    this.bookingParameterService.bookingParameters.setActioncode({ type: codetype.promo, text: code });
    this.bookingParameterService.saveBookingParameters(this.bookingParameterService.bookingParameters);
    this.updateCart(CartEventType.promocodeUpdated);
  }

  public setCouponcode(code: string) {
    this.cart.couponCode = code;
    this.bookingParameterService.bookingParameters.setActioncode({ type: codetype.coupon, text: code });
    this.bookingParameterService.saveBookingParameters(this.bookingParameterService.bookingParameters);
    this.updateCart(CartEventType.couponcodeUpdated);
  }

  updatePersonSkill(person: Person, skill: string) {
    person.skill = skill;
    this.updateCart(CartEventType.personSkillChanged, person);
  }

  /**
   * Returns true if personAges in params is a correct array of number-values,
   * false if not.
   */
  private isPersonAgesValid(params: ParamMap): boolean {
    if (!params.has('personAges[]')) {
      return false;
    }
    const personAges = params.getAll('personAges[]');
    if (!Array.isArray(personAges)) {
      return false;
    }
    for (let i = 0; i < personAges.length; ++i) {
      if (!Number(personAges[i])) {
        return false;
      }
    }
    return true;
  }

  /**
   * this function sanitizes the given params and will patch/replace
   * missing or broken ones from bookingParameters
   * @param params ParamMap containing all necessary cart parametes like startDate, persons, etc.
   */
  private sanitizeParams(params: ParamMap, shop: Shop, shopProducts: Product[]): CartInterface {
    let cart: CartInterface = { persons: [] };
    if (params.has('cart')) {
      // parse and use cart data if applicable
      cart = JSON.parse(params.get('cart'));
    }

    // no people in cart, get them
    if (!cart.persons.length) {
      // use personAges[] if applicable
      if (this.isPersonAgesValid(params)) {
        cart.persons = params.getAll('personAges[]').map(age => ({
          age: +age,
          products: []
        }));
      }
      // use booking params if applicable
      else if (this.bookingParameterService.bookingParameters.ages?.length) {
        cart.persons = this.bookingParameterService.bookingParameters.ages.map(age => ({
          age: +age,
          products: []
        }));
      }
      // fallback to default 'adult' in shop
      else {
        const defaultAI = shop.ageIntervals.find(aI => aI.is_default);
        cart.persons.push({ age: defaultAI ? defaultAI.start : this.defaultAgeAdult, products: [] });
      }
    }

    // check persons' ages and products
    else {
      cart.persons.forEach(person => {
        if (!Number(person.age) || person.age < 1 || person.age > 99) {
          person.age = this.defaultAgeAdult;
        }

        // remove product from cart if is unavailable in shop
        person.products = person.products.filter(product =>
          shopProducts.some(p => p.definitionId === product.definitionId)
        );

        // remove unavailable addons from products
        person.products.forEach(product => {
          const shopProduct = shopProducts.find(p => p.definitionId === product.definitionId);
          product.addons.filter(addon => shopProduct.addons.some(a => +a.definitionId === addon));
        });
      });
    }
    cart.startDate =
      params.get('startDate') ?? this.dateToString(this.bookingParameterService.bookingParameters.start);
    cart.endDate =
      params.get('endDate') ?? this.dateToString(this.bookingParameterService.bookingParameters.end);
    // incomplete or no date range, use default
    this.validateAndAdjustCartDates(cart);

    this.bookingParameterService.bookingParameters.start = new Date(cart.startDate);
    this.bookingParameterService.bookingParameters.end = new Date(cart.endDate);

    // use cart coupon if set
    if (cart.couponCode) {
      this.bookingParameterService.bookingParameters.setActioncode({
        type: codetype.coupon,
        text: cart.couponCode
      });
    }
    // else use bookingParam coupon if set
    else if (this.bookingParameterService.bookingParameters.getCouponcode()) {
      cart.couponCode = this.bookingParameterService.bookingParameters.getCouponcode().text;
    }

    // use cart promo if set
    if (cart.promotionCode) {
      this.bookingParameterService.bookingParameters.setActioncode({
        type: codetype.promo,
        text: cart.promotionCode
      });
    }
    // else use bookingParam promo if set
    else if (this.bookingParameterService.bookingParameters.getPromocode()) {
      cart.promotionCode = this.bookingParameterService.bookingParameters.getPromocode().text;
    }

    // update the booking params
    this.bookingParameterService.saveBookingParameters(this.bookingParameterService.bookingParameters);

    return cart;
  }

  private validateAndAdjustCartDates(cart: CartInterface) {
    // Convert the startDate and endDate from strings to Date objects
    const start = new Date(cart.startDate);
    const end = new Date(cart.endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start < today || end < start) {
      const bookingParams = this.bookingParameterService.createNewBookingParameters();

      cart.startDate = this.dateToString(bookingParams.start);
      cart.endDate = this.dateToString(bookingParams.end);
      return;
    }

    // Check if the date range is more than 14 days
    const dayDifference = (end.getTime() - start.getTime()) / (1000 * 3600 * 24);
    if (dayDifference > 14) {
      // keep the start date and make the end date=start date +14 days
      cart.endDate = this.dateToString(new Date(start.getTime() + 14 * 24 * 60 * 60 * 1000));
    }
  }

  private dateToString(date: Date) {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  }

  filterProductsOnStop(persons: Person[]) {
    if (persons.some(pers => pers.productEntities.some(p => p.hasStopProduct))) {
      persons.forEach(pers => {
        pers.productEntities.filter(p => !p.hasStopProduct);
      });
    }
  }

  /**
   * update discount free state of child free logic on all persons in cart
   */
  private updateChildFree(cart: Cart) {
    if (this.noMansSki === 'v1') {
      cart.persons.forEach(p => {
        if (p.discounts) {
          p.discounts.isFree = cart.isChildFreeActiveFor(cart.persons, p) ? true : false;
        }
      });
    } else {
      // in v2, we consider the selected person as part of the carts persons
      // so we can correctly show if the product would be for free or not
      const persons = [...cart.persons, cart.selectedPerson];
      persons.forEach(p => {
        if (p.discounts) {
          p.discounts.isFree = cart.isChildFreeActiveFor(persons, p) ? true : false;
        }
      });
    }
  }

  /**
   * not every cart update requires fetching of new discounts.
   * This function updates persons discounts if possible and returns
   * a flag to let you know if further discount fetch is necessary
   * @param event
   */
  private smartDiscountUpdate(
    event: AnyCartEvent
  ): Observable<{ event: AnyCartEvent; isDiscountFetchRequired: boolean }> {
    let isDiscountFetchRequired = true;

    switch (event.eventType) {
      case CartEventType.promocodeUpdated: {
        this.mapDiscountsToPersons();
        break;
      }
      case CartEventType.personAdded: {
        // we added a person but we stayed withing the group discount range AND
        // already know the discount for the added ageInterval
        const matchingDiscount = event.cart.persons.find(
          p => p.ageInterval?.id === event.cart.persons[event.cart.persons.length - 1].ageInterval?.id
        )?.discounts;
        if (
          matchingDiscount &&
          !this.isGroupDiscountForShopDifferent(
            event.cart.shop,
            event.cart.persons.length,
            event.cart.persons.length - 1
          )
        ) {
          event.cart.persons[event.cart.persons.length - 1].discounts = { ...matchingDiscount };
          this.updateChildFree(event.cart);
          isDiscountFetchRequired = false;
        } else {
          this.mapDiscountsToPersons();
        }

        break;
      }
      case CartEventType.personRemoved: {
        // Update child free, keep discounts if group discounts stayed
        this.updateChildFree(event.cart);
        if (
          !this.isGroupDiscountForShopDifferent(
            event.cart.shop,
            event.cart.persons.length,
            event.cart.persons.length + 1
          )
        ) {
          if (this.noMansSki === 'v1') {
            isDiscountFetchRequired = false;
          } else {
            if (
              !this.isGroupDiscountForShopDifferent(
                event.cart.shop,
                event.cart.persons.length + 1,
                event.cart.persons.length + 2
              )
            ) {
              isDiscountFetchRequired = false;
            } else {
              // we stepped below the group discount threshold, remove displayed product discount
              event.cart.selectedPerson.discounts = undefined;
            }
          }
        } else {
          this.mapDiscountsToPersons();
        }
        break;
      }
      case CartEventType.personAgeUpdated: {
        // when new person age discounts are already known, use them, update child free
        const index = event.cart.persons.indexOf(event.person);
        const mapDiscount = event.cart.persons.find(
          (p, i) =>
            index !== i &&
            p.ageInterval.id === event.person.ageInterval.id &&
            (this.noMansSki === 'v1' ||
              !this.isGroupDiscountForShopDifferent(
                event.cart.shop,
                event.cart.persons.length,
                event.cart.persons.length + 1
              ))
        )?.discounts;
        if (mapDiscount) {
          event.person.discounts = { ...mapDiscount };
          isDiscountFetchRequired = false;
        } else {
          if (this.noMansSki === 'v1') {
            event.person.discounts = undefined;
          } else {
            event.cart.selectedPerson.discounts = undefined;
          }
        }
        this.updateChildFree(event.cart);

        break;
      }
      case CartEventType.productRemoved: {
        // removing a product does not affect any discounts besides possible child free
        this.updateChildFree(event.cart);
        isDiscountFetchRequired = false;
        break;
      }
      case CartEventType.productUpdated: {
        // adding a product does not affect any discounts besides possible child free
        if (this.noMansSki === 'v1') {
          this.updateChildFree(event.cart);
          isDiscountFetchRequired = false;
        } else {
          // adding a product is like adding a person
          // we added a person but we stayed withing the group discount range AND
          // already know the discount for the added ageInterval
          const matchingDiscount = event.cart.persons.find(
            p => p.ageInterval?.id === event.cart.persons[event.cart.persons.length - 1].ageInterval?.id
          )?.discounts;
          if (
            matchingDiscount &&
            !this.isGroupDiscountForShopDifferent(
              event.cart.shop,
              event.cart.persons.length,
              event.cart.persons.length - 1
            )
          ) {
            event.cart.persons[event.cart.persons.length - 1].discounts = { ...matchingDiscount };

            if (
              !this.isGroupDiscountForShopDifferent(
                event.cart.shop,
                event.cart.persons.length,
                event.cart.persons.length + 1
              )
            ) {
              // if with one extra the prices also didn't change, update selected person
              event.cart.selectedPerson.discounts = { ...matchingDiscount };
            } else {
              event.cart.selectedPerson.discounts = undefined;
            }

            this.updateChildFree(event.cart);
          } else {
            event.cart.selectedPerson.discounts = undefined;
            this.mapDiscountsToPersons();
          }
        }
        break;
      }
    }

    return of({ event: event, isDiscountFetchRequired: isDiscountFetchRequired });
  }

  /**
   * pulls discounts from servers and maps them onto people. This is a slow and heavy
   * call and should be only done if necessary.
   * @param locale locale of the customer
   * @param currency for which currency to pull and calculate (ALWAYS EUR, talk to RS)
   * @returns
   */
  private fetchDiscountsFromCore(
    locale: Locale = Locale.en,
    currency = SupportedCurrency.EUR
  ): Observable<Cart> {
    const request: LegacyDynamicPricesRequestDto = {
      shopId: this.cart.shopId,
      locale,
      customerCountry: undefined,
      currency,
      rentalPeriod: {
        from: this.cart.startDateString,
        to: this.cart.endDateString
      },
      person: this.getPersonsFromCart(),
      coupons: {},
      promoCodes: {},
      insurance: this.getInsurancesFromCart() as unknown as { [key: string]: LegacyInsuranceDto },
      partnerPrefix: this.platformService.getCurrentPartner()
    };

    if (this.cart.couponCode) {
      request.coupons = { 1: { code: this.cart.couponCode } };
    }
    if (this.cart.promotionCode) {
      request.promoCodes = { 1: { code: this.cart.promotionCode } };
    }

    let request2 = null;
    if (this.noMansSki === 'v2' && this.cart.persons.length >= 1) {
      request2 = { ...request };
      // make sure our person object is an independent copy
      request2.person = Object.assign({}, request.person);
      // and then add an additional person
      request2.person[Object.keys(request2.person).length] = {
        age: this.cart.selectedPerson.age,
        equipment: {}
      };
    }

    return this.platformInformationService.customerCountryCode$.pipe(
      // make sure to only call discounts when you know the customers country
      switchMap(resp => {
        const country = this.cookieService.get('cart_customer_country') || resp;
        request.customerCountry = country as Locale;
        if (request2) {
          request2.customerCountry = country as Locale;
        }
        // TODO: we'd need at least two calls here. One with and one without the "upcoming" person
        return forkJoin([
          this.coreService
            .apiCorePaymentDynamicPriceInfo(request, undefined, false, {
              responseType: 'json',
              headers: new HttpHeaders().set('Cache-Control', 'no-cache')
            })
            .pipe(delayedRetry(100, 3)),
          request2
            ? this.coreService
                .apiCorePaymentDynamicPriceInfo(request2, undefined, false, {
                  responseType: 'json',
                  headers: new HttpHeaders().set('Cache-Control', 'no-cache')
                })
                .pipe(delayedRetry(100, 3))
            : of(null)
          // 1: add one request for each age category available in shop
          // 2: combine the discounts into one discount matrix for the selected person
        ]).pipe(
          map(discounts => {
            this.mapDiscountsToPersons(discounts[0], discounts[1]);
            return this.cart;
          }),
          catchError(() => {
            return of(null);
          })
        );
      })
    );
  }

  private convertRates(rates: { [key: string]: string | number }): { [key: string]: number } {
    const convertedRates: { [key: string]: number } = {};

    for (const key in rates) {
      let value = rates[key];
      if (typeof value !== 'number') {
        value = parseFloat(value);
        if (isNaN(value)) {
          throw new Error(`Invalid number for key: ${key}`);
        }
      }
      convertedRates[key] = value;
    }
    return convertedRates;
  }

  private mapDiscountsToPersons(
    discounts?: LegacyDynamicPricesResponseDto,
    personLessDiscount?: LegacyDynamicPricesResponseDto
  ) {
    if (discounts) {
      this.cart.averageQuality = discounts.averageQuality;
      this.cart.fee = discounts.fee;
      this.cart.promotionCodeReduction = discounts.promotionCodeReduction;
      this.cart.totalPaidOnlineBothTypes = discounts.totalPaidOnlineBothTypes;
      this.cart.totalPaidCash = discounts.totalPaidCash;
      this.cart.totalPaidOnlineAfterCoupons = discounts.totalPaidOnlineAfterCoupons;
      this.cart.discountAbsolute = discounts.discountAbsolute;
      this.cart.totalDeductedFromCoupons = discounts.totalDeductedFromCoupons;
      this.cart.discountPercentage = discounts.discountPercentage;
      this.cart.paymentTypeChoiceAvailable = discounts.paymentTypeChoiceAvailable;

      // protection/global item prices
      this.cart.isGuarantyBookable = discounts.isGuarantyBookable;
      this.cart.useGuarantyInterface = discounts.useGuarantyInterface;
      this.cart.potentialCancellationInsuranceRaw =
        discounts.potentialCancellationInsuranceRaw as unknown as AmountInterface;
      this.cart.potentialGuarantyRaw = discounts.potentialGuarantyRaw as unknown as AmountInterface;
      this.cart.potentialInsuranceRaw = discounts.potentialInsuranceRaw as unknown as AmountInterface;
      this.cart.potentialInsurancePlusRaw = discounts.potentialInsurancePlusRaw as unknown as AmountInterface;

      if (discounts.euroBasedRates) {
        this.cart.euroBasedRates = {
          rates: this.convertRates(discounts.euroBasedRates),
          code: 'EUR'
        };
      }
    } else {
      if (this.noMansSki === 'v2') {
        this.cart.selectedPerson.discounts = null;
      }
    }

    this.cart.persons.forEach((person, index) => {
      person.discounts = discounts?.priceInfo[index];
    });

    // noMansSki "show correct discount" logic here
    if (this.cart.persons.length < 1 && this.cart.selectedPerson) {
      // empty cart and no people? Map default discount to selected person
      this.cart.selectedPerson.discounts = discounts?.priceInfo[0];
      this.updateCart(CartEventType.discountsUpdated);
    } else if (personLessDiscount?.priceInfo[this.cart.persons.length]) {
      this.cart.selectedPerson.discounts = personLessDiscount?.priceInfo[this.cart.persons.length];
      this.updateCart(CartEventType.discountsUpdated);
    } else {
      this.updateCart(CartEventType.discountsUpdated);
    }
  }

  private getInsurancesFromCart(): { definitionId: number }[] {
    return [
      this.cart.cancellationSelected,
      this.cart.insuranceSelected,
      this.cart.insurancePlusSelected
    ].reduce((acc, value, index) => {
      if (value) acc.push({ definitionId: index + 1 });
      return acc;
    }, []);
  }

  private getPersonsFromCart(): { [key: string]: LegacyPersonDto | undefined } {
    const result = this.cart.persons.reduce((acc, person, index) => {
      acc[index] = {
        age: person.age,
        equipment: this.getEquipmentsFromProducts(person.productEntities)
      };
      return acc;
    }, {});

    if (this.noMansSki === 'v2') {
      if (this.cart.persons.length < 1) {
        return {
          0: {
            age: this.cart.selectedPerson.age,
            equipment: this.getEquipmentsFromProducts(this.cart.selectedPerson.productEntities)
          }
        };
      }
    }
    return result;
  }

  private getEquipmentsFromProducts(products: Product[]): { [key: string]: LegacyEquipmentDto | undefined } {
    const result = products.reduce((acc, product, index) => {
      const product_: LegacyEquipmentDto = {
        definitionId: product.definitionId,
        accessories: this.getProductAccessories(product.addons)
      };
      acc[index] = product_;
      return acc;
    }, {});

    return result;
  }

  private getProductAccessories(addons: Addon[]): { [key: string]: LegacyAccessoryDto } {
    const result = addons.reduce((acc, addon, index) => {
      const addon_: LegacyAccessoryDto = {
        definitionId: addon.definitionId
      };
      acc[index] = addon_;
      return acc;
    }, {});
    return result;
  }

  /**
   * quick hand shortcut to send out cart events. Unfortunately not entirely typesafe as
   * one can set the eventType without handing over the required person, product or replacedProduct data
   * TODO: make this typesafe(r) by not being lazy (ask for AnyCartEvent in the signature)
   * @param eventType
   * @param person
   * @param product
   * @param replacedProduct
   */
  public updateCart(eventType: CartEventType, person?: Person, product?: Product, replacedProduct?: Product) {
    const evt = { cart: this.cart, eventType, person, product, replacedProduct };
    if (!person) {
      delete evt.person;
    }
    if (!product) {
      delete evt.product;
    }
    if (!replacedProduct) {
      delete evt.replacedProduct;
    }
    this.cart$.next(evt as AnyCartEvent);
  }
}
